// TEACHER TOUR SCENES — inert replicas of the teacher dashboard (Courses → a course with Student Work and New assignment → one
// submission → the roster). Presentation only; every spotlit element carries the `data-tour` name the teacher tour names.
import { PenLine } from "lucide-react";
import { TDEMO as T } from "./teacherTour";

const primary = "inline-flex items-center justify-center rounded-sm bg-[#8C3A2A] px-4 py-2 text-[13px] font-medium text-white";
const ghost = "inline-flex items-center justify-center rounded-sm border border-stone-300 px-4 py-2 text-[13px] text-stone-700 bg-white";
const field = "w-full border border-stone-300 rounded-sm px-3 py-2 text-sm mb-2 bg-white text-stone-800";
const lbl = "block text-[12px] text-stone-600 mb-1";
const Mono = ({ children }) => <div className="text-[11px] font-mono uppercase tracking-wider text-stone-400 mb-1">{children}</div>;

function Shell({ crumbs, children }) {
  return (
    <div className="min-h-full bg-stone-50">
      <header className="border-b border-stone-200 bg-white">
        <nav className="max-w-5xl mx-auto px-6 pt-3 flex items-center gap-1 text-[12px]"><span className="px-3 py-1 rounded-sm bg-stone-100 text-stone-900">Courses</span><span className="px-3 py-1 text-stone-500">Student view</span></nav>
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between text-sm">
          <div className="flex items-center gap-3"><span className="grid place-items-center h-6 w-6 rounded-sm bg-[#8C3A2A]"><PenLine className="h-3.5 w-3.5 text-white" /></span><span className="font-serif text-[15px] text-stone-900">The Writing Studio</span><span className="text-stone-300">|</span>{crumbs.map((c, i) => <span key={c} className={i === crumbs.length - 1 ? "text-stone-900 font-medium" : "text-stone-500"}>{i > 0 && <span className="text-stone-300 mr-3">/</span>}{c}</span>)}</div>
          <div className="flex items-center gap-3 text-[13px] text-stone-500"><span>{T.teacher} · teacher</span><span>Sign out</span></div>
        </div>
      </header>
      <main className="max-w-6xl mx-auto px-6 py-8">{children}</main>
    </div>
  );
}

const Pill = ({ s }) => {
  const c = s === "Completed" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : s === "In progress" ? "bg-amber-50 text-amber-800 border-amber-200" : "bg-stone-100 text-stone-600 border-stone-200";
  return <span className={`text-[11px] px-2 py-0.5 rounded-full border ${c}`}>{s}</span>;
};
const Late = () => <span className="text-[10px] font-mono uppercase tracking-wider text-[#8C3A2A]">Late</span>;

function SceneCourses() {
  return (
    <Shell crumbs={["Courses"]}>
      <h1 className="font-serif text-3xl text-stone-900 mb-6">My Courses</h1>
      <div className="grid sm:grid-cols-2 gap-4">
        {T.courses.map((c, i) => (
          <div key={c.title} data-tour={i === 0 ? "t-course-card" : undefined} className="text-left bg-white border border-stone-200 rounded-md p-5">
            <div className="font-serif text-lg text-stone-900">{c.title}</div>
            <div className="mt-1 text-[13px] text-stone-500">{c.students} students · {c.assignments} assignments</div>
            <div className="mt-2 text-[12px] font-mono">Join code: <span className="text-[#8C3A2A]">{c.code}</span></div>
            <div className="mt-2 text-[12px] text-[#8C3A2A]">Open course →</div>
          </div>
        ))}
      </div>
      <div data-tour="t-new-course" className="mt-8 bg-white border border-stone-200 rounded-md p-5 max-w-md">
        <div className="font-medium text-stone-800 mb-3">New course</div>
        <div className={field}>English 9 — Period 1</div>
        <span className={primary}>Create course</span>
      </div>
    </Shell>
  );
}

function AssignmentForm() {
  return (
    <div data-tour="t-new-asg" className="mt-4 bg-white border border-stone-200 rounded-md p-4 w-full max-w-md">
      <div className="text-[13px] font-medium text-stone-700 mb-3">＋ New assignment</div>
      <div className={field}>{T.assignment.title}</div>
      <div className={`${field} h-20 text-stone-700`}>{T.assignment.prompt}</div>
      <label className={lbl}>Due date (optional)</label>
      <div className="border border-stone-300 rounded-sm px-3 py-2 text-sm mb-3 bg-white inline-block text-stone-800">11/14/2026</div>
      <div data-tour="t-path">
        <label className={lbl}>How should students begin?</label>
        <div className={field}>Strengthen existing writing ▾</div>
        <p className="text-[11.5px] text-stone-500 -mt-1 mb-3 leading-snug">Students type in writing they already have; TWS reads what each part is doing and strengthens it with them — the whole, the paragraphs, then sentences.</p>
        <label className={lbl}>What structure should students use?</label>
        <div className={field}>Flexible ▾</div>
      </div>
      <div data-tour="t-context" className="mt-1">
        <label className="flex items-center gap-2 text-[12.5px] text-stone-700 mb-2"><span className="h-3.5 w-3.5 rounded-sm border border-[#8C3A2A] bg-[#8C3A2A]" /> Students must use assigned material</label>
        <div className={field}>Chapter 4 of the course reader; the two editorials from class.</div>
        <label className={lbl}>Requirements</label>
        <div className="flex flex-wrap gap-1.5 mb-2">{["Use evidence", "Compare or contrast", "Explain causes", "Address a counterargument"].map((r, i) => <span key={r} className={`text-[11px] px-2 py-0.5 rounded-full border ${i < 2 || i === 3 ? "border-[#8C3A2A] text-[#8C3A2A] bg-[#8C3A2A]/5" : "border-stone-200 text-stone-500"}`}>{r}</span>)}</div>
        <label className={lbl}>Guidance for TWS (students do not see this)</label>
        <div className={`${field} text-stone-700`}>Push students to connect their example back to the claim; do not accept a narrative without a point.</div>
      </div>
      <span className={primary}>Create assignment</span>
    </div>
  );
}

function SceneCourse() {
  const c = T.courses[0];
  return (
    <Shell crumbs={["Courses", c.title]}>
      <div className="flex flex-wrap items-baseline gap-3"><h1 className="font-serif text-3xl text-stone-900">{c.title}</h1><span className="text-[12px] text-stone-500 underline underline-offset-2">Rename</span></div>
      <div data-tour="t-join" className="mt-2 inline-flex flex-wrap items-center gap-3 pr-2">
        <p className="text-[13px] font-mono">Join code: <span className="text-[#8C3A2A] text-[15px] tracking-wider">{c.code}</span></p>
        <span className="text-[12px] text-stone-500 underline underline-offset-2">Copy code</span>
        <span className={`${primary} !py-1.5 !px-3 text-[13px]`}>Send code to students by email</span>
      </div>
      <p className="mt-2 text-[12.5px] text-stone-500">Students sign in (or create a student account), choose <em>My classes</em>, and enter this code.</p>
      <div data-tour="t-tabs" className="mt-4 inline-flex flex-wrap items-center gap-1 bg-stone-100 border border-stone-200 rounded-md p-1 text-sm max-w-full">
        <span className="px-4 py-1.5 rounded-sm bg-white text-stone-900 shadow-sm font-medium">Student Work</span><span className="px-4 py-1.5 text-stone-600">Development</span><span className="px-4 py-1.5 text-stone-600">Roster</span><span className="px-4 py-1.5 text-stone-600">Assessments</span><span className="px-4 py-1.5 text-stone-600">Writing in This Course</span>
      </div>
      <p className="mt-2 text-[12px] text-stone-400 max-w-2xl">Work that arrives after a due date is marked <Late />. Students with unfinished work are emailed a reminder two days before each due date and again on the day.</p>
      <div data-tour="t-grid" className="mt-6 bg-white border border-stone-200 rounded-lg overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="border-b border-stone-200 bg-stone-50">
              <th className="text-left px-4 py-3 text-[12px] font-mono uppercase tracking-wider text-stone-400">Student</th>
              {T.columns.map((a, i) => (
                <th key={a.title} className="px-3 py-3 text-center align-bottom min-w-[150px]">
                  <div className="text-[12px] font-medium text-stone-700">{a.title}</div>
                  <div className={`mt-1 text-[11px] font-normal ${a.due === "No due date" ? "text-stone-300" : "text-stone-500"}`}>{a.due}</div>
                  <div data-tour={i === 1 ? "t-asg-actions" : undefined} className="mt-1 flex justify-center gap-2 text-[11px] font-normal text-stone-500 underline underline-offset-2"><span>Edit</span><span>Reassign</span><span>Archive</span><span className="text-stone-400">Delete</span></div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {T.rows.map((r) => (
              <tr key={r.name} className="border-b border-stone-100 last:border-0">
                <td className="px-4 py-3 whitespace-nowrap font-serif text-lg text-stone-900">{r.name}</td>
                {r.cells.map((s, i) => (
                  <td key={i} className="px-3 py-3 text-center">
                    {s === "Not started" ? <span className="text-stone-300">—</span> : (
                      <span className={`inline-flex items-center gap-2 rounded-full border px-2.5 py-1 ${r.late[i] ? "border-[#8C3A2A]/50" : "border-stone-200"}`}><Pill s={s} />{r.late[i] && <Late />}<span className="text-[12px] text-stone-500">Open →</span></span>
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="mt-4 bg-white border border-stone-200 rounded-md p-4 w-full max-w-md text-[13px] font-medium text-stone-700">↻ Use an assignment from another course</div>
      <AssignmentForm />
    </Shell>
  );
}

function SceneSubmission() {
  const c = T.courses[0];
  return (
    <Shell crumbs={["Courses", c.title, "Alex Jordan"]}>
      <p className="text-[13px] text-stone-500 mb-3">← Back to student work</p>
      <h1 className="font-serif text-3xl text-stone-900">Alex Jordan</h1>
      <p className="text-[13px] text-stone-500 mb-6">{T.assignment.title}</p>
      <div data-tour="t-versions">
        <div className="grid md:grid-cols-2 gap-4">
          <div className="bg-white border border-stone-200 rounded-md p-4"><Mono>Initial</Mono><p className="text-sm text-stone-800">{T.initial}</p></div>
          <div className="bg-white border border-stone-200 rounded-md p-4"><Mono>Final</Mono><p className="text-sm text-stone-800">{T.final}</p></div>
        </div>
        <span className={`${ghost} mt-4`}>Show revision history (6)</span>
        <section className="mt-6 bg-white border border-stone-200 rounded-md p-4 max-w-2xl">
          <Mono>Writing path</Mono>
          <div className="grid sm:grid-cols-2 gap-x-6 gap-y-3 text-[13px] text-stone-800">
            <div><Mono>Route</Mono>Strengthen existing writing · a short multi-paragraph response</div>
            <div><Mono>Status</Mono>Complete · 6 revisions</div>
            <div><Mono>Main point as TWS read it</Mono>Schools should require community service because the requirement itself gives students experiences they would not otherwise choose.</div>
            <div><Mono>Levels reached</Mono>Composition 3 / 4 · Thinking Structure 5 / 7</div>
            <div className="sm:col-span-2"><Mono>Student's reflection</Mono>{T.reflection}</div>
          </div>
        </section>
      </div>
      <div data-tour="t-feedback" className="mt-6 bg-white border border-stone-200 rounded-md p-4 max-w-2xl">
        <div className="text-sm font-medium text-stone-800 mb-2">Feedback to student</div>
        <div className="w-full border border-stone-300 rounded-sm px-3 py-2 text-sm h-24 text-stone-800 bg-white">{T.feedback}</div>
        <span className={`${primary} mt-2`}>Save feedback</span>
      </div>
    </Shell>
  );
}

function SceneRoster() {
  const c = T.courses[0];
  return (
    <Shell crumbs={["Courses", c.title, "Roster"]}>
      <div className="flex items-baseline justify-between"><h1 className="font-serif text-3xl text-stone-900">Roster</h1><span className="text-[12px] text-stone-500 underline underline-offset-2">Export spreadsheet</span></div>
      <p className="mt-1 text-[13px] text-stone-600 max-w-2xl">Names and emails of everyone in {c.title}. Type a grade in any cell. Composition (0–4) and Thinking Structure (1–7) are filled in from TWS's analysis of each student's latest version.</p>
      <div data-tour="t-roster" className="mt-5 bg-white border border-stone-200 rounded-lg overflow-x-auto">
        <table className="w-full text-[13px] border-collapse">
          <thead className="text-[10px] font-mono uppercase tracking-wider text-stone-400">
            <tr className="border-b border-stone-200 bg-stone-50"><th rowSpan={2} className="px-4 py-2 text-left align-bottom">Student</th>{T.columns.map((a) => <th key={a.title} colSpan={3} className="px-3 py-2 text-center border-l border-stone-100">{a.title}</th>)}</tr>
            <tr className="border-b border-stone-200 bg-stone-50">{T.columns.map((a) => <Fragment3 key={a.title} />)}</tr>
          </thead>
          <tbody>
            {T.rows.map((r) => (
              <tr key={r.name} className="border-b border-stone-100 last:border-0">
                <th scope="row" className="px-4 py-2.5 font-normal text-left"><div className="text-stone-900">{r.name}</div><div className="text-[11.5px] text-stone-500">{r.email}</div></th>
                {r.grade.map((g, i) => (
                  <Cells key={i} g={g} comp={r.comp[i]} think={r.think[i]} />
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Shell>
  );
}
const Fragment3 = () => <><th className="px-3 py-1.5 text-center border-l border-stone-100">Grade</th><th className="px-3 py-1.5 text-center">Composition</th><th className="px-3 py-1.5 text-center">Thinking</th></>;
const Cells = ({ g, comp, think }) => (
  <>
    <td className="px-3 py-2 text-center border-l border-stone-100"><span className={`inline-block w-14 border rounded-sm px-2 py-1 text-[12.5px] ${g ? "border-stone-300 text-stone-800" : "border-dashed border-stone-200 text-stone-300"}`}>{g || "—"}</span></td>
    <td className="px-3 py-2 text-center text-stone-700">{comp === null ? <span className="text-stone-300">—</span> : `${comp} / 4`}</td>
    <td className="px-3 py-2 text-center text-stone-700">{think === null ? <span className="text-stone-300">—</span> : `${think} / 7`}</td>
  </>
);

export const TEACHER_SCENES = { "t-courses": SceneCourses, "t-course": SceneCourse, "t-submission": SceneSubmission, "t-roster": SceneRoster };
